import { useCallback, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import axios from "axios"
import { useTranslation } from "react-i18next"
import { Building2, MapPin, Pencil, Trash2 } from "lucide-react"
import AppLayout from "@/components/app/AppLayout"
import { FeatureGate } from "@/components/app/FeatureGate"
import { PilotBanner } from "@/components/app/PilotBanner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Switch } from "@/components/ui/switch"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { API_BASE } from "@/config/api"
import { useAuth } from "@/components/auth-provider"
import { toast } from "sonner"

const API = API_BASE

const emptyFacilityForm = () => ({
  name: "",
  address: "",
  phone: "",
  code: "",
})

export default function Network() {
  const { t } = useTranslation()
  const { refreshUser } = useAuth()
  const [net, setNet] = useState(null)
  const [loading, setLoading] = useState(true)
  const [orgName, setOrgName] = useState("")
  const [editingOrg, setEditingOrg] = useState(false)
  const [facilityForm, setFacilityForm] = useState(emptyFacilityForm())
  const [editingFacilityId, setEditingFacilityId] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await axios.get(`${API}/network/organization`)
      setNet(data)
      if (data.organization && !editingOrg) {
        setOrgName(data.organization.name)
      }
    } catch {
      toast.error(t("app.network.toast.loadFailed"))
    } finally {
      setLoading(false)
    }
  }, [t, editingOrg])

  useEffect(() => {
    load()
  }, [load])

  const createOrg = async () => {
    if (!orgName.trim()) return toast.error(t("app.network.toast.orgNameRequired"))
    try {
      await axios.post(`${API}/network/organizations`, { name: orgName.trim() })
      toast.success(t("app.network.toast.orgCreated"))
      await load()
    } catch (err) {
      const msg = err.response?.data?.detail
      toast.error(typeof msg === "string" ? msg : t("app.network.toast.saveFailed"))
    }
  }

  const saveOrg = async () => {
    if (!orgName.trim() || !net?.organization?.id) return
    try {
      await axios.patch(`${API}/network/organizations/${net.organization.id}`, {
        name: orgName.trim(),
      })
      setEditingOrg(false)
      toast.success(t("app.network.toast.orgUpdated"))
      await load()
    } catch (err) {
      const msg = err.response?.data?.detail
      toast.error(typeof msg === "string" ? msg : t("app.network.toast.saveFailed"))
    }
  }

  const resetFacilityForm = () => {
    setFacilityForm(emptyFacilityForm())
    setEditingFacilityId(null)
  }

  const saveFacility = async () => {
    if (!facilityForm.name.trim()) return toast.error(t("app.network.toast.facilityNameRequired"))
    if (!net?.organization?.id) return
    try {
      if (editingFacilityId) {
        await axios.patch(`${API}/network/facilities/${editingFacilityId}`, {
          name: facilityForm.name.trim(),
          address: facilityForm.address.trim(),
          phone: facilityForm.phone.trim(),
          code: facilityForm.code.trim(),
        })
        toast.success(t("app.network.toast.facilityUpdated"))
      } else {
        await axios.post(`${API}/network/facilities`, {
          organization_id: net.organization.id,
          name: facilityForm.name.trim(),
          address: facilityForm.address.trim(),
          phone: facilityForm.phone.trim(),
          code: facilityForm.code.trim(),
        })
        toast.success(t("app.network.toast.facilityAdded"))
      }
      resetFacilityForm()
      await load()
    } catch (err) {
      const msg = err.response?.data?.detail
      toast.error(typeof msg === "string" ? msg : t("app.network.toast.saveFailed"))
    }
  }

  const startEditFacility = (f) => {
    setEditingFacilityId(f.id)
    setFacilityForm({
      name: f.name || "",
      address: f.address || "",
      phone: f.phone || "",
      code: f.code || "",
    })
  }

  const deleteFacility = async (id) => {
    try {
      await axios.delete(`${API}/network/facilities/${id}`)
      if (editingFacilityId === id) resetFacilityForm()
      toast.success(t("app.network.toast.facilityDeleted"))
      await load()
    } catch (err) {
      const msg = err.response?.data?.detail
      toast.error(typeof msg === "string" ? msg : t("app.network.toast.saveFailed"))
    }
  }

  const setCurrentLocation = async (facilityId) => {
    try {
      await axios.patch(`${API}/network/current-facility`, { facility_id: facilityId })
      toast.success(t("app.network.toast.locationSet"))
      await Promise.all([load(), refreshUser?.()])
    } catch (err) {
      const msg = err.response?.data?.detail
      toast.error(typeof msg === "string" ? msg : t("app.network.toast.saveFailed"))
    }
  }

  const toggleFacilityActive = async (f) => {
    try {
      await axios.patch(`${API}/network/facilities/${f.id}`, { active: !f.active })
      await load()
    } catch {
      toast.error(t("app.network.toast.saveFailed"))
    }
  }

  const org = net?.organization
  const facilities = net?.facilities || []
  const currentId = net?.current_facility_id
  const summary = net?.summary || { location_count: 0, active_locations: 0 }

  return (
    <FeatureGate
      feature="multi_location"
      title={t("app.network.gateTitle")}
      description={t("app.network.gateDescription")}
    >
      <AppLayout title={t("app.network.title")}>
        <p className="text-xs text-muted-foreground mb-4 max-w-2xl">
          {t("app.network.intro")}{" "}
          <Link to="/app/settings" className="underline underline-offset-2">
            {t("app.nav.settings")}
          </Link>
          {" · "}
          <Link to="/app/referrals" className="underline underline-offset-2">
            {t("app.nav.referrals")}
          </Link>
        </p>

        <PilotBanner className="mb-4 max-w-2xl">{t("app.network.pilotHint")}</PilotBanner>

        {loading && (
          <p className="text-sm text-muted-foreground">{t("app.network.loading")}</p>
        )}

        {!loading && !org && (
          <section className="rounded-xl border border-border p-6 bg-card space-y-4 max-w-md">
            <h3 className="text-sm font-medium flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              {t("app.network.createOrg")}
            </h3>
            <p className="text-xs text-muted-foreground">{t("app.network.createOrgHelp")}</p>
            <div>
              <Label className="text-xs">{t("app.network.fieldOrgName")}</Label>
              <Input
                className="mt-1"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder={t("app.network.orgNamePlaceholder")}
              />
            </div>
            <Button className="rounded-full" onClick={createOrg}>
              {t("app.network.createOrgBtn")}
            </Button>
          </section>
        )}

        {!loading && org && (
          <>
            <div className="grid sm:grid-cols-3 gap-3 mb-6 max-w-3xl">
              <article className="rounded-xl border border-border bg-card p-4">
                <p className="text-xs text-muted-foreground">{t("app.network.metrics.locations")}</p>
                <p className="font-display text-2xl mt-1">{summary.location_count}</p>
              </article>
              <article className="rounded-xl border border-border bg-card p-4">
                <p className="text-xs text-muted-foreground">{t("app.network.metrics.active")}</p>
                <p className="font-display text-2xl mt-1">{summary.active_locations}</p>
              </article>
              <article className="rounded-xl border border-border bg-card p-4">
                <p className="text-xs text-muted-foreground">{t("app.network.metrics.current")}</p>
                <p className="text-sm font-medium mt-2 line-clamp-2">
                  {net.current_facility?.name || t("app.network.noCurrent")}
                </p>
              </article>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 max-w-5xl">
              <div className="space-y-6">
                <section className="rounded-xl border border-border p-6 bg-card space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-medium">{t("app.network.organization")}</h3>
                    {!editingOrg ? (
                      <Button variant="ghost" size="sm" className="rounded-full h-8" onClick={() => setEditingOrg(true)}>
                        <Pencil className="h-3.5 w-3.5 mr-1" />
                        {t("app.network.edit")}
                      </Button>
                    ) : null}
                  </div>
                  {editingOrg ? (
                    <>
                      <Input value={orgName} onChange={(e) => setOrgName(e.target.value)} />
                      <div className="flex gap-2">
                        <Button size="sm" className="rounded-full" onClick={saveOrg}>
                          {t("app.network.save")}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="rounded-full"
                          onClick={() => {
                            setEditingOrg(false)
                            setOrgName(org.name)
                          }}
                        >
                          {t("app.network.cancel")}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <p className="text-lg font-medium">{org.name}</p>
                  )}
                  {net.practice?.name && (
                    <p className="text-xs text-muted-foreground">
                      {t("app.network.linkedPractice", { name: net.practice.name })}
                    </p>
                  )}
                </section>

                <section className="rounded-xl border border-border p-6 bg-card space-y-4">
                  <h3 className="text-sm font-medium">
                    {editingFacilityId ? t("app.network.editLocation") : t("app.network.addLocation")}
                  </h3>
                  <div>
                    <Label className="text-xs">{t("app.network.fieldLocationName")}</Label>
                    <Input
                      className="mt-1"
                      value={facilityForm.name}
                      onChange={(e) => setFacilityForm((f) => ({ ...f, name: e.target.value }))}
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">{t("app.network.fieldCode")}</Label>
                      <Input
                        className="mt-1"
                        value={facilityForm.code}
                        onChange={(e) => setFacilityForm((f) => ({ ...f, code: e.target.value }))}
                        placeholder="BUC-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{t("app.network.fieldPhone")}</Label>
                      <Input
                        className="mt-1"
                        value={facilityForm.phone}
                        onChange={(e) => setFacilityForm((f) => ({ ...f, phone: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">{t("app.network.fieldAddress")}</Label>
                    <Input
                      className="mt-1"
                      value={facilityForm.address}
                      onChange={(e) => setFacilityForm((f) => ({ ...f, address: e.target.value }))}
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button className="rounded-full" onClick={saveFacility}>
                      {editingFacilityId ? t("app.network.save") : t("app.network.addLocationBtn")}
                    </Button>
                    {editingFacilityId && (
                      <Button variant="ghost" className="rounded-full" onClick={resetFacilityForm}>
                        {t("app.network.cancel")}
                      </Button>
                    )}
                  </div>
                </section>
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-medium">{t("app.network.locationsList")}</h3>
                <ul className="divide-y divide-border rounded-xl border border-border bg-card text-sm">
                  {facilities.map((f) => (
                    <li key={f.id} className="px-4 py-4 space-y-2">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-medium flex items-center gap-2 flex-wrap">
                            {f.name}
                            {currentId === f.id && (
                              <Badge variant="default" className="text-[10px]">
                                {t("app.network.currentBadge")}
                              </Badge>
                            )}
                            {f.active === false && (
                              <Badge variant="secondary" className="text-[10px]">
                                {t("app.network.inactiveBadge")}
                              </Badge>
                            )}
                          </p>
                          {f.code && (
                            <p className="text-xs text-muted-foreground mt-0.5">{f.code}</p>
                          )}
                          {f.address && (
                            <p className="text-xs text-muted-foreground mt-1 flex items-start gap-1">
                              <MapPin className="h-3 w-3 shrink-0 mt-0.5" />
                              {f.address}
                            </p>
                          )}
                          {f.phone && (
                            <p className="text-xs text-muted-foreground mt-0.5">{f.phone}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Switch
                            checked={f.active !== false}
                            onCheckedChange={() => toggleFacilityActive(f)}
                            aria-label={t("app.network.toggleActive")}
                          />
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {currentId !== f.id && f.active !== false && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full h-8"
                            onClick={() => setCurrentLocation(f.id)}
                          >
                            {t("app.network.setCurrent")}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="rounded-full h-8"
                          onClick={() => startEditFacility(f)}
                        >
                          {t("app.network.edit")}
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>{t("app.network.deleteTitle")}</AlertDialogTitle>
                              <AlertDialogDescription>{t("app.network.deleteBody")}</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t("app.network.cancel")}</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteFacility(f.id)}>
                                {t("app.network.confirmDelete")}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </li>
                  ))}
                  {!facilities.length && (
                    <li className="px-4 py-8 text-center text-muted-foreground">
                      {t("app.network.noLocations")}
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </>
        )}
      </AppLayout>
    </FeatureGate>
  )
}

